import React, { useEffect, useState } from "react";
import CircularBar from "../../../components/chart-bars/CircularBar.jsx";
import "../../../styles/StoreBrowserIn.css";
import Report from "./Report.jsx";
import Proof from "./Proof.jsx";
import { Link, useLocation, useParams } from "react-router-dom";
import ScrollDown from "../../../components/ScrollDown.jsx";
import BreadCrumbLayout from "../../../components/common/BreadCrumbLayout.jsx";
import { Outlet } from "react-router-dom";
import { Spin } from "antd";
import Trends from "./Trends.jsx";
import { useFetchFunction } from "../../../utils/hooks/UseFetchUrl";
import useScoreScale from "../../../utils/hooks/useScoreScale";

const StoreBrowserIn = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { storeId, tabName } = useParams();
  const location = useLocation();
  const initialStoreData = location.state?.storeData;
  const { data: fetchedData, getData, error } = useFetchFunction();
  const data =
    initialStoreData || (Array.isArray(fetchedData) ? null : fetchedData);
  const [tabData, setTabData] = useState(tabName || "trends");

  useEffect(() => {
    if (!initialStoreData && storeId) {
      getData(`store/${storeId}`);
    }
  }, [storeId, initialStoreData]);

  useEffect(() => {
    if (tabName) {
      setTabData(tabName);
    }
  }, [tabName]);

  const score = data?.get_total_percentage?.score || 0;
  const {
    text: scoreText,
    color,
    scoreLegend,
  } = useScoreScale(score, data?.client?.score_scale);

  if (!data) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        {error ? <p>Failed to load store details.</p> : <Spin size="large" />}
      </div>
    );
  }

  return (
    <div className="px-4">
      <div className="inMain">
        <div
          className="audit-first no-print"
          style={{ paddingTop: 0, margin: "1rem 0 0.5rem 0", width: "auto" }}
        >
          <div className="rbar px-3 text-start d-flex align-items-center">
            <Link to="/storebrowser" style={{ marginTop: "0px" }}>
              Store Browser
            </Link>
            <BreadCrumbLayout />
          </div>
        </div>
        <div className="hero-first mx-auto row py-2 d-flex justify-content-between">
          <div className="col-md-8">
            <div className="overall d-flex align-items-center gap-2">
              <p className="my-3">Overall Score</p>
              <div className="score-per" style={{ backgroundColor: color }}>
                <p className="ms-1">
                  {data.get_total_percentage?.score || "N/A"}%
                </p>
              </div>
            </div>
            <div className="audit-details mt-2 d-flex">
              <p className="ms-2">Store Details</p>
            </div>
            <div className="store-table d-flex mt-4 ">
              <div className="table-right">
                <table>
                  <tbody>
                    <tr>
                      <td className="leftText1">
                        <strong>Store Name</strong>
                      </td>
                      <td className="rightText1">{data?.name}</td>
                    </tr>
                    <tr>
                      <td className="leftText1">
                        <strong>City</strong>
                      </td>
                      <td className="rightText1">{data?.city?.name}</td>
                    </tr>
                    <tr>
                      <td className="leftText1">
                        <strong>Store Address</strong>
                      </td>
                      <td className="rightText1">{data?.address}</td>
                    </tr>
                    <tr>
                      <td className="leftText1">
                        <strong>State</strong>
                      </td>
                      <td className="rightText1">{data?.city?.state}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="hero-right col-md-3 me-3 meter d-flex flex-column align-items-center justify-content-center">
            <p className="overallPara">Overall Store Score</p>
            <CircularBar value={score} color={color} />
            <div className="store-score d-flex justify-content-center align-items-center flex-column">
              <p className="scorePara">
                Your Store Score Is <span style={{ color }}>{scoreText}</span>
              </p>
              <div className="stats">
                <p className="m-0">What this statistics mean?</p>
              </div>
              <div className="grades-div d-flex flex-wrap gap-2 mt-3">
                {scoreLegend.map((range) => (
                  <div
                    key={range.key}
                    style={{
                      width: range.key === "excellent" ? "55px" : undefined,
                    }}
                    className="stats-mean d-flex flex-column align-items-center justify-content-center"
                  >
                    <p className="grade m-1">{range.text}</p>
                    <p className="percent m-0">
                      {range.min}% to{" "}
                      {range.max === 100 ? "above" : `${range.max}%`}
                    </p>
                    <div
                      style={{
                        width: "41px",
                        height: "8px",
                        borderRadius: "10px",
                        backgroundColor: range.bgColor,
                      }}
                      className="mt-1 stats-div"
                    ></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="tab-container">
          <div
            onClick={() => {
              setTabData("trends");
            }}
            className={`trends df ${tabData === "trends" ? "active" : ""}`}
          >
            <i className="bi bi-bar-chart-fill"></i>
            <p className="textOfTab">Trends</p>
          </div>
          <div
            onClick={() => {
              setTabData("reports");
            }}
            className={`reports df ${tabData === "reports" ? "active" : ""}`}
          >
            <i className="bi bi-calendar-minus"></i>
            <p className="textOfTab">Reports</p>
          </div>
          <div
            onClick={() => {
              setTabData("proof");
            }}
            className={`proof df ${tabData === "proof" ? "active" : ""}`}
          >
            <i className="bi bi-arrow-left-right"></i>
            <p className="textOfTab">Proof Comparison</p>
          </div>
        </div>
        <div className="trp">
          {tabData === "trends" ? (
            <Trends storeData={data} />
          ) : tabData === "reports" ? (
            <Report storeData={data} />
          ) : (
            <Proof storeData={data} />
          )}
        </div>
        <Outlet />
      </div>
      <ScrollDown />
    </div>
  );
};

export default StoreBrowserIn;
